import axios, { AxiosInstance, AxiosResponse } from "axios";
import {
  AuthResponse,
  UserCreate,
  UserLogin,
  User,
  Website,
  WebsiteCreate,
  WebsiteUpdate,
  AnalyticsEvent,
  AnalyticsQuery,
  AnalyticsStats,
  Itinerary,
  ItineraryCreate,
  ItineraryUpdate,
  ItineraryStats,
  ClaimVerificationRequest,
  ClaimVerificationResponse,
  TranslationRequest,
  TranslationResponse,
} from "../types";
import { storageService } from "./storage";

const API_URL =
  (process.env.REACT_APP_API_URL || "http://localhost:3000") + "/api/v1";

class ApiService {
  private api: AxiosInstance;

  constructor() {
    this.api = axios.create({
      baseURL: API_URL,
      headers: {
        "Content-Type": "application/json",
      },
    });

    // Add request interceptor to include auth token
    this.api.interceptors.request.use(
      async (config) => {
        const token = await storageService.getAuthToken();
        if (token) {
          config.headers.Authorization = `Bearer ${token}`;
        }
        return config;
      },
      (error) => {
        return Promise.reject(error);
      }
    );

    // Add response interceptor to handle auth errors
    this.api.interceptors.response.use(
      (response) => response,
      async (error) => {
        if (error.response?.status === 401) {
          await storageService.clearAuth();
          window.location.href = "/login";
        }
        return Promise.reject(error);
      }
    );
  }

  // Auth endpoints
  async register(userData: UserCreate): Promise<AuthResponse> {
    try {
      console.log("API: Registering user with data:", userData);
      console.log(
        "API: Making request to:",
        `${this.api.defaults.baseURL}/auth/register`
      );

      const response: AxiosResponse<AuthResponse> = await this.api.post(
        "/auth/register",
        userData
      );

      console.log("API: Registration response:", response.data);
      return response.data;
    } catch (error) {
      console.error("API: Registration error:", error);
      throw error;
    }
  }

  async login(credentials: UserLogin): Promise<AuthResponse> {
    const response: AxiosResponse<AuthResponse> = await this.api.post(
      "/auth/login",
      credentials
    );
    return response.data;
  }

  async getCurrentUser(): Promise<{ success: boolean; user: User }> {
    const response = await this.api.get("/auth/me");
    return response.data;
  }

  // Website endpoints
  async getWebsites(): Promise<{ success: boolean; websites: Website[] }> {
    const response = await this.api.get("/websites");
    return response.data;
  }

  async createWebsite(
    websiteData: WebsiteCreate
  ): Promise<{ success: boolean; website: Website; message: string }> {
    const response = await this.api.post("/websites", websiteData);
    return response.data;
  }

  async updateWebsite(
    id: number,
    websiteData: WebsiteUpdate
  ): Promise<{ success: boolean; website: Website; message: string }> {
    const response = await this.api.put(`/websites/${id}`, websiteData);
    return response.data;
  }

  async deleteWebsite(
    id: number
  ): Promise<{ success: boolean; message: string }> {
    const response = await this.api.delete(`/websites/${id}`);
    return response.data;
  }

  // Analytics endpoints
  async getAnalyticsEvents(query: AnalyticsQuery): Promise<{
    events: AnalyticsEvent[];
    total: number;
    limit: number;
    offset: number;
  }> {
    const response = await this.api.get("/analytics/events", { params: query });
    return response.data;
  }

  async getAnalyticsStats(
    query: Omit<
      AnalyticsQuery,
      "limit" | "offset" | "order_by" | "order_direction"
    >
  ): Promise<AnalyticsStats> {
    const response = await this.api.get("/analytics/stats", { params: query });
    return response.data;
  }

  // Itinerary endpoints
  async getItineraries(): Promise<{
    success: boolean;
    data: Itinerary[];
    count: number;
  }> {
    const response = await this.api.get("/itineraries");
    return response.data;
  }

  async getItinerary(
    id: number
  ): Promise<{ success: boolean; data: Itinerary }> {
    const response = await this.api.get(`/itineraries/${id}`);
    return response.data;
  }

  async getItineraryStats(): Promise<{
    success: boolean;
    data: ItineraryStats;
  }> {
    const response = await this.api.get("/itineraries/stats");
    return response.data;
  }

  async createItinerary(
    itineraryData: ItineraryCreate
  ): Promise<{ success: boolean; data: Itinerary; message: string }> {
    const response = await this.api.post("/itineraries", itineraryData);
    return response.data;
  }

  async getMyItineraries(): Promise<{
    success: boolean;
    data: Itinerary[];
    count: number;
  }> {
    const response = await this.api.get("/itineraries/my");
    return response.data;
  }

  async getMyItineraryStats(): Promise<{
    success: boolean;
    data: ItineraryStats;
  }> {
    const response = await this.api.get("/itineraries/my/stats");
    return response.data;
  }

  async updateItinerary(
    id: number,
    itineraryData: ItineraryUpdate
  ): Promise<{ success: boolean; data: Itinerary; message: string }> {
    const response = await this.api.put(`/itineraries/${id}`, itineraryData);
    return response.data;
  }

  async deleteItinerary(
    id: number
  ): Promise<{ success: boolean; message: string }> {
    const response = await this.api.delete(`/itineraries/${id}`);
    return response.data;
  }

  async verifyClaim(
    request: ClaimVerificationRequest
  ): Promise<{ success: boolean; data: ClaimVerificationResponse }> {
    const response = await this.api.post("/claims/verify", request);
    return response.data;
  }

  async verifyMegoClaim(
    request: ClaimVerificationRequest
  ): Promise<{ success: boolean; data: ClaimVerificationResponse }> {
    const response = await this.api.post("/claims/mego/verify", request);
    return response.data;
  }

  // File upload endpoints
  async uploadImage(
    file: File,
    folder: string = "images"
  ): Promise<{
    success: boolean;
    data: {
      url: string;
      key: string;
      bucket: string;
    };
    message: string;
  }> {
    const formData = new FormData();
    formData.append("file", file);

    const response = await this.api.post(
      `/upload/image?folder=${folder}`,
      formData,
      {
        headers: {
          "Content-Type": "multipart/form-data",
        },
      }
    );
    return response.data;
  }

  async uploadAudio(file: File): Promise<{
    success: boolean;
    data: {
      url: string;
      key: string;
      bucket: string;
    };
    message: string;
  }> {
    const formData = new FormData();
    formData.append("file", file);

    const response = await this.api.post("/upload/audio", formData, {
      headers: {
        "Content-Type": "multipart/form-data",
      },
    });
    return response.data;
  }

  async deleteFile(
    url: string
  ): Promise<{ success: boolean; message: string }> {
    const response = await this.api.delete("/upload/delete", {
      data: { url },
    });
    return response.data;
  }

  // Translation endpoints
  async translateText(
    request: TranslationRequest
  ): Promise<{ success: boolean; data: TranslationResponse; message: string }> {
    const response = await this.api.post("/translate", request);
    return response.data;
  }

  async translateBatch(
    texts: string[],
    sourceLanguage: string,
    targetLanguage: string
  ): Promise<{ success: boolean; data: TranslationResponse[]; message: string }> {
    const response = await this.api.post("/translate/batch", {
      texts,
      sourceLanguage,
      targetLanguage,
    });
    return response.data;
  }

  async getSupportedLanguages(): Promise<{
    success: boolean;
    data: Record<string, string>;
    message: string;
  }> {
    const response = await this.api.get("/translate/languages");
    return response.data;
  }
}

export const apiService = new ApiService();
